import { ApplicationDataStore } from '../application/ApplicationDataStore';
import { DataItem } from '@servicetitan/financing-utils-shared';
import { extractLocation } from './extractLocation';
import { ReviewCard } from './ReviewCard';
import startCase from 'lodash/startCase';
import { ImperativeForm } from '../common/Form';
import { useRef } from 'react';
import { CoApplicantContainer } from '../co-applicant';
import { useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { usePersonalInfoReviewFields } from './useReviewFields';

export const CoApplicant = () => {
    const { applicationData } = ApplicationDataStore.useStore();
    const coApplicant = applicationData.coApplicant?.hasCoApplicant
        ? applicationData.coApplicant.coApplicant
        : null;
    const idIssueLocation = extractLocation(coApplicant?.applicantId);
    const mailingAddress =
        coApplicant?.isAddressSameAsPrimary === false
            ? coApplicant?.address
            : applicationData.mailingAddress?.primaryApplicant?.address;
    const formattedMailingAddress = `${mailingAddress?.street}<br> ${mailingAddress?.city}, ${mailingAddress?.state} ${mailingAddress?.zip}`;
    const formRef = useRef<ImperativeForm>(null);
    const { hasIdFields, useAnnualIncome } = useProviderConfig();
    const { fieldOrder, fieldRegistry } = usePersonalInfoReviewFields();

    return (
        <ReviewCard title="Co-Applicant" formRef={formRef}>
            <ReviewCard.View>
                {/* Special handling for Name field - combine firstName and lastName */}
                <DataItem
                    label="Name"
                    text={`${coApplicant?.firstName ?? ''} ${coApplicant?.lastName ?? ''}`.trim()}
                />
                {fieldOrder.map(fieldKey => {
                    const field = fieldRegistry[fieldKey];
                    if (!field) {
                        return null;
                    }

                    // Skip firstName and lastName as they're handled above
                    if (fieldKey === 'firstName' || fieldKey === 'lastName') {
                        return null;
                    }

                    // Get review label (can be function or string)
                    const label =
                        typeof field.reviewLabel === 'function'
                            ? field.reviewLabel({ useAnnualIncome } as any)
                            : field.reviewLabel;

                    // Get review value
                    const value = field.reviewValue(coApplicant);
                    if (!value) {
                        return null; // Skip if no data
                    }

                    return (
                        <DataItem
                            key={fieldKey}
                            label={label}
                            text={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        />
                    );
                })}
                {hasIdFields && (
                    <>
                        <DataItem
                            label="Id Type"
                            text={startCase(coApplicant?.applicantId?.applicantIdDocumentType)}
                        />
                        <DataItem label="Id Number" text={coApplicant?.applicantId?.documentId} />
                        <DataItem
                            label="ID Issue Date"
                            text={coApplicant?.applicantId?.issueDate}
                        />
                        <DataItem
                            label="ID Expiration Date"
                            text={coApplicant?.applicantId?.expirationDate}
                        />
                        <DataItem label={idIssueLocation.label} text={idIssueLocation.name} />
                    </>
                )}
                <DataItem label="Mailing Address" text={formattedMailingAddress} />
            </ReviewCard.View>
            <ReviewCard.Edit>
                <CoApplicantContainer formRef={formRef} />
            </ReviewCard.Edit>
        </ReviewCard>
    );
};
